import type { TourChapter } from '@flobi/asset-library/tours';
import { TOUR_ANCHORS as A } from './tourAnchors';
import { FILE_PAGE } from './tourPages';

/**
 * Artwork's six chapters (PLAN-2026-10-26-artwork-onboarding-tours.md), one Get started step each:
 * what it is → where it is → do it → why it matters. Steps are the kit's (machine.js). Proofs are
 * real actions: Artwork's hint signals, where the person lands, or what the editor shows (the
 * selection bar says what is chosen, `data-asset-type`).
 */

const chosenType = () => (typeof document === 'undefined' ? null : document.querySelector('[data-tour="artwork.selectionBar"]')?.getAttribute('data-asset-type') ?? null);
const shown = (selector: string) => typeof document !== 'undefined' && Boolean(document.querySelector(selector));
const NOT_A_SHAPE = new Set(['artboard', 'text', 'image', 'multiple']);

/**
 * The page to draw on: the canvas between the chrome (the top bar, the rail, the dock, Properties
 * when it is out). Steps that draw leave this open and block the rest (26 Oct: "outside actions are
 * still workable"). The canvas itself fills the window under the chrome, so it can't be named.
 */
const pageArea = () => {
  if (typeof document === 'undefined') return null;
  const box = (selector: string) => document.querySelector(selector)?.getBoundingClientRect() ?? null;
  const head = box('.artwork-topbar');
  const rail = box('[data-tour="artwork.rail"]');
  const dock = box('[data-tour="artwork.dock"]');
  const side = box('aside[aria-label="Properties"]');
  const left = rail ? rail.right + 8 : 0;
  const top = head ? head.bottom + 8 : 0;
  const right = side ? side.left - 8 : window.innerWidth;
  const bottom = dock ? dock.top - 8 : window.innerHeight;
  return right > left && bottom > top ? { left, top, width: right - left, height: bottom - top } : null;
};
const GENERATION = 'section[aria-label="Generation controls"]';

/**
 * Words typed on the page. The Text tool types into one off-screen field labelled "Text"
 * (editor/textEditing.ts), and the selection bar is not up while the tool is in hand, so the step
 * hears the field: each key there is told on as TYPED.
 */
export const TYPED = 'artwork:tour-typed';
if (typeof document !== 'undefined') {
  document.addEventListener(
    'input',
    (event) => {
      if ((event.target as Element | null)?.matches?.('textarea[aria-label="Text"]')) window.dispatchEvent(new Event(TYPED));
    },
    true,
  );
}
const ANIMATE = 'section[aria-label="Animate"]';
/**
 * Asks the editor to let go of a choice Animate can't take (EditorPage): a picture left loose off the
 * page, as the AI tour's often lands, made Animate say "Select an artboard" and the step stuck (30 Oct).
 */
export const ANIMATABLE = 'artwork:tour-animatable';
// The panel a rail kind opens beside the rail: it joins the ring, so the card goes beside both.
const ASSETS_PANEL = '.library-browser';

export const WELCOME: TourChapter = {
  id: 'welcome',
  where: 'home',
  steps: [
    {
      id: 'what',
      media: 'welcome',
      title: 'Welcome to Artwork',
      body: 'Make posts, pages and videos here. AI helps when you want it, and your brand is built in.',
      next: 'Next',
    },
    {
      id: 'composer',
      target: A.composer,
      title: 'Start with words',
      body: 'Type what you want to make. Size picks its shape, and Brand picks its look.',
      next: 'Next',
      optional: true,
    },
    {
      id: 'new-file',
      target: A.newFile,
      title: 'Or start blank',
      body: 'Press New File and pick a size, or press a size beside it. A blank file opens.',
      until: { path: [FILE_PAGE, (m) => ({ filePath: m[0] })] },
    },
    {
      // The fast first result (30 Oct, the Learn formula): one press and a finished design is on the page.
      id: 'template',
      page: 'file',
      target: A.railTemplates,
      also: [ASSETS_PANEL],
      keepOpen: true,
      control: 'Templates',
      title: 'Start from a template',
      body: 'Press Templates, then press any design. It lands on your page, ready to change.',
      wait: 'Waiting for a template',
      // The Templates panel's press or drag says asset.placed (EditorPage libraryActions).
      until: { signals: ['asset.placed'] },
    },
    {
      id: 'done',
      page: 'file',
      media: 'editor',
      title: 'That’s your first design',
      body: 'Change any word, picture or colour on it. It saves as you go.',
      next: 'Finish',
    },
  ],
};

export const FIRST_DESIGN: TourChapter = {
  id: 'first-design',
  where: 'file',
  steps: [
    {
      id: 'what',
      media: 'design',
      title: 'Your first design',
      body: 'The bar at the bottom holds your tools. Each arrow opens more.',
      next: 'Next',
    },
    {
      id: 'shape',
      target: A.shape,
      placeAround: A.dock,
      control: 'Shape',
      title: 'Draw a shape',
      body: 'Press Shape, then drag it out on the page. Its arrow has the other shapes.',
      wait: 'Waiting for your shape',
      // The drawing happens on the page: it stays usable, the rest of the editor is blocked.
      open: [pageArea],
      until: { check: () => { const type = chosenType(); return Boolean(type && !NOT_A_SHAPE.has(type)); } },
    },
    {
      id: 'colour',
      target: A.selectionBar,
      // The bar is there only while the shape is chosen: gone (a pause, a press elsewhere), the tour asks for a shape again.
      needs: 'shape',
      control: 'Colour',
      title: 'Give it a colour',
      body: 'Press Colour on this bar and pick one. Your brand’s colours come first.',
      next: 'Next',
      until: { signals: ['colour.brandPicked', 'colour.offBrand', 'colour.typed', 'colour.eyedropped'] },
    },
    {
      id: 'text',
      target: A.text,
      placeAround: A.dock,
      control: 'Text',
      title: 'Add some words',
      body: 'Press Text and pick Point type, then click the page and type.',
      wait: 'Waiting for your words',
      open: [pageArea],
      until: { events: [TYPED], check: () => chosenType() === 'text' },
    },
    {
      id: 'done',
      media: 'designDone',
      title: 'That’s a design',
      body: 'Select moves and sizes anything. ••• on the bar holds the rest, Properties first.',
      next: 'Finish',
    },
  ],
};

export const ADD: TourChapter = {
  id: 'add',
  where: 'file',
  steps: [
    {
      id: 'what',
      media: 'add',
      title: 'Add pictures and graphics',
      body: 'Templates, graphics, your pictures and your brand sit on the rail at the left.',
      next: 'Next',
    },
    {
      id: 'rail',
      target: A.rail,
      media: 'rail',
      title: 'The rail',
      body: 'It holds Generate, Elements, Templates, Graphics, My Stuff and Brand. Upload adds your own files.',
      next: 'Next',
    },
    {
      id: 'brand',
      target: A.railBrand,
      control: 'Brand',
      title: 'Your brand',
      body: 'Its logos, colours and type are ready to place.',
      next: 'Next',
      optional: true,
    },
    {
      id: 'place',
      target: A.rail,
      also: [ASSETS_PANEL],
      media: 'place',
      title: 'Put one on the page',
      body: 'Open Templates, Graphics, My Stuff or Brand, then press or drag any tile. Upload adds a picture from your computer.',
      wait: 'Waiting for something on the page',
      // The rail and its panel are the ring; a tile dragged onto the page passes through the blockers.
      // Every panel's press or drag says asset.placed (EditorPage libraryActions); Upload says image.placed.
      until: { signals: ['asset.placed', 'image.placed', 'mystuff.placed', 'brand.placed'] },
    },
    {
      id: 'done',
      media: 'addDone',
      title: 'It’s on the page',
      body: 'Replace a picture any time. It keeps its size and place.',
      next: 'Finish',
    },
  ],
};

export const AI: TourChapter = {
  id: 'ai',
  where: 'file',
  steps: [
    {
      id: 'what',
      media: 'ai',
      title: 'Make it with AI',
      body: 'Describe a picture and Artwork makes it, right on your page.',
      next: 'Next',
    },
    {
      id: 'open',
      target: A.railGenerate,
      control: 'Generate',
      title: 'Open Generate',
      body: 'Press Generate on the rail.',
      until: { check: () => shown(GENERATION) },
    },
    {
      id: 'describe',
      select: () => GENERATION,
      needs: 'open',
      keepOpen: true,
      title: 'Make a picture',
      body: 'We wrote the words for you. Press Generate; it’s free in the tour.',
      wait: 'Making your picture',
      // The run is free here (freeGeneration.ts): a picture made for the tour lands as a real one, and says generation.added.
      until: { signals: ['generation.added'] },
    },
    {
      id: 'done',
      media: 'aiDone',
      title: 'Edit with AI',
      body: 'Choose a picture and the bar shows Edit with AI. Remove background comes first, and Adjust is free.',
      next: 'Finish',
    },
  ],
};

export const ANIMATE_CHAPTER: TourChapter = {
  id: 'animate',
  where: 'file',
  steps: [
    {
      id: 'what',
      media: 'animate',
      title: 'Bring it to life',
      body: 'One press animates the whole page. Preview plays it, and it exports as a video.',
      next: 'Next',
    },
    {
      id: 'open',
      target: A.animate,
      control: 'Animate',
      title: 'Open Animate',
      body: 'Press Animate in the bottom bar.',
      prepare: () => void window.dispatchEvent(new Event(ANIMATABLE)),
      until: { check: () => shown(ANIMATE) },
    },
    {
      id: 'recipe',
      select: () => ANIMATE,
      // Inside the Animate bar: closed, or gone after Resume, the tour goes back to Press Animate.
      needs: 'open',
      keepOpen: true,
      title: 'Pick a look',
      body: 'Each card is a way to move. Press one: it moves what’s chosen, or the whole page.',
      wait: 'Waiting for a pick',
      // A page look (animate.recipe), or with a layer chosen, one of its moves (animate.layerEdited).
      until: { signals: ['animate.recipe', 'animate.layerEdited'] },
    },
    {
      id: 'play',
      // The bar's Preview button (AnimateBar's animate-preview), ringed on its own.
      select: () => `${ANIMATE} .animate-preview`,
      needs: 'open',
      keepOpen: true,
      control: 'Preview',
      title: 'Watch it',
      body: 'Press Preview at the top right of the bar, or Space.',
      wait: 'Waiting for Preview',
      // A press only. A look picked plays by itself, and counting that skipped this step before it
      // was read (26 Oct walk); while it plays the button reads Pause, and pressing that counts too.
      until: { signals: ['animate.played', 'shortcut.animatePlay'] },
    },
    {
      id: 'done',
      media: 'animateDone',
      title: 'It moves',
      body: 'Choose one thing and open Animate again. It shows that thing’s Comes In, Keeps Moving and Goes Out.',
      next: 'Finish',
    },
  ],
};

export const SHARE_EXPORT: TourChapter = {
  id: 'share-export',
  where: 'file',
  steps: [
    {
      id: 'what',
      media: 'share',
      title: 'Share and export',
      body: 'Work on it together. Or take it anywhere as a picture, a PDF or a video.',
      next: 'Next',
    },
    {
      id: 'share',
      target: A.share,
      control: 'Share',
      title: 'Share with people',
      body: 'Invite people to edit or view, or copy its link.',
      next: 'Next',
      optional: true,
    },
    {
      id: 'comment',
      target: A.comment,
      control: 'Comment',
      title: 'Comments on the page',
      body: 'Leave notes right on the page for whoever you share it with.',
      next: 'Next',
      optional: true,
    },
    {
      id: 'export',
      target: A.export,
      control: 'Export',
      title: 'Export it',
      body: 'Press Export, pick a format, then press its Export button.',
      // Artwork says export.done as the button is pressed (ExportPanel `tell`), not when the file lands.
      wait: 'Waiting for your export',
      until: { signals: ['export.done'] },
    },
    {
      id: 'done',
      media: 'shareDone',
      title: 'Yours to use',
      body: 'Tips on Home has a short guide to everything else.',
      next: 'Finish',
    },
  ],
};

export const ALL_CHAPTERS: TourChapter[] = [WELCOME, FIRST_DESIGN, ADD, AI, ANIMATE_CHAPTER, SHARE_EXPORT];
